import { useState, useEffect, useRef } from "react";
import {
  User,
  Crown,
  CreditCard,
  LogOut,
  ChevronRight,
  Flame,
  Settings,
  MessageSquare,
  Send,
  Users,
  PlayCircle,
  Camera,
  Loader2,
  FileText,
  Info,
  Lock,
  Eye,
  EyeOff,
  ExternalLink,
  Bug,
  RotateCcw,
  RefreshCw,
  ChevronDown,
  Trash2,
  Shield,
  X,
} from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { calculateTier, TIER_COLORS } from "@/types/evolve";
import type { UserProfile } from "@/types/evolve";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { ImageCropModal } from "@/components/ImageCropModal";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FriendsSection } from "@/components/FriendsSection";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { usePlatformPurchase } from "@/hooks/usePlatformPurchase";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

// Helper function to get initials from name
function getInitials(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

interface ProfileTabProps {
  profile: UserProfile;
  onUpgrade: () => void;
  onLogout: () => void;
  onOpenSettings?: () => void;
  onOpenLeaderboard?: () => void;
  onManageSubscription?: () => void;
  onOpenReferral?: () => void;
  onReplayTour?: () => void;
  isEarlyBird?: boolean;
  totalWorkouts?: number;
  onAvatarUpdate?: (url: string) => void;
  subscriptionEnd?: string | null;
  subscriptionPlan?: "monthly" | "yearly" | null;
}

export function ProfileTab({
  profile,
  onUpgrade,
  onLogout,
  onOpenSettings,
  onOpenLeaderboard,
  onManageSubscription,
  onOpenReferral,
  onReplayTour,
  isEarlyBird,
  totalWorkouts = 0,
  onAvatarUpdate,
  subscriptionEnd,
  subscriptionPlan,
}: ProfileTabProps) {
  const { currentTier, skulls } = calculateTier(totalWorkouts);
  const [feedback, setFeedback] = useState("");
  const [feedbackType, setFeedbackType] = useState<"bug" | "feature" | "general">("general");
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [userRank, setUserRank] = useState<number | null>(null);
  const [referralCount, setReferralCount] = useState(0);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [userBadgeCount, setUserBadgeCount] = useState(0);

  // Crop modal state
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);

  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChangePassword = async () => {
    if (!newPassword || !confirmNewPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setPasswordLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast.success("Password updated successfully!");
      setShowPasswordModal(false);
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error: any) {
      toast.error(error.message || "Failed to update password");
    } finally {
      setPasswordLoading(false);
    }
  };

  // Get user's badge count for comparison
  useEffect(() => {
    const fetchBadgeCount = async () => {
      if (!profile.id) return;
      const { count } = await supabase
        .from("user_badges")
        .select("*", { count: "exact", head: true })
        .eq("user_id", profile.id);
      setUserBadgeCount(count || 0);
    };
    fetchBadgeCount();
  }, [profile.id]);

  useEffect(() => {
    const fetchRank = async () => {
      if (!profile.id) return;
      try {
        // Get workout counts for all users using the RPC function
        const { data: workoutCounts, error } = await supabase.rpc("get_leaderboard_workout_counts");

        if (error) throw error;

        // Count users with more workouts than current user
        const usersWithMoreWorkouts = (workoutCounts || []).filter(
          (row: { total_workouts: number }) => row.total_workouts > totalWorkouts,
        ).length;

        setUserRank(usersWithMoreWorkouts + 1);
      } catch (error) {
        console.error("Error fetching rank:", error);
      }
    };
    fetchRank();
  }, [profile.id, totalWorkouts]);

  useEffect(() => {
    const fetchReferralCount = async () => {
      if (!profile.id) return;
      try {
        const { count } = await supabase
          .from("referrals")
          .select("*", { count: "exact", head: true })
          .eq("referrer_id", profile.id);

        setReferralCount(count || 0);
      } catch (error) {
        console.error("Error fetching referral count:", error);
      }
    };
    fetchReferralCount();
  }, [profile.id]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    // Create object URL for cropping
    const imageUrl = URL.createObjectURL(file);
    setImageToCrop(imageUrl);
    setShowCropModal(true);

    // Clear the input so the same file can be selected again
    event.target.value = "";
  };

  const handleCroppedImage = async (croppedBlob: Blob) => {
    if (!profile.id) return;

    setUploadingAvatar(true);

    try {
      const filePath = `${profile.id}/avatar.jpg`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, croppedBlob, { upsert: true, contentType: "image/jpeg" });

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from("avatars").getPublicUrl(filePath);

      // Add cache buster to force refresh
      const urlWithCacheBuster = `${publicUrl}?t=${Date.now()}`;
      onAvatarUpdate?.(urlWithCacheBuster);
      toast.success("Profile picture updated!");
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploadingAvatar(false);
      // Clean up object URL
      if (imageToCrop) {
        URL.revokeObjectURL(imageToCrop);
        setImageToCrop(null);
      }
    }
  };

  const handleSendFeedback = async () => {
    if (!feedback.trim()) {
      toast.error("Please enter your feedback");
      return;
    }

    if (!profile.id) {
      toast.error("You must be logged in to submit feedback");
      return;
    }

    setSendingFeedback(true);
    try {
      const { error } = await supabase.from("feedback").insert({
        user_id: profile.id,
        type: feedbackType,
        message: feedback.trim(),
        user_name: profile.name || null,
      });

      if (error) throw error;

      toast.success("Thanks for your feedback! 🙏");
      setFeedback("");
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setSendingFeedback(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto px-4 py-6 pb-36 space-y-6">
      {/* Profile Header */}
      <section className="glass-card rounded-2xl p-6 text-center space-y-4">
        <div className="relative inline-block">
          <button
            onClick={() => avatarInputRef.current?.click()}
            disabled={uploadingAvatar}
            className={`w-24 h-24 rounded-full bg-gradient-to-br ${TIER_COLORS[currentTier]} flex items-center justify-center mx-auto evolve-glow-intense overflow-hidden relative group transition-transform hover:scale-105`}
          >
            {profile.avatarUrl ? (
              <img src={profile.avatarUrl} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <span className="text-2xl font-bold text-foreground font-display">{getInitials(profile.name)}</span>
            )}

            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Camera className="w-6 h-6 text-white" />
            </div>

            {/* Loading overlay */}
            {uploadingAvatar && (
              <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            )}
          </button>

          {/* Hidden file input */}
          <input ref={avatarInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />

          {profile.isPremium && (
            <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full evolve-gradient flex items-center justify-center border-2 border-background">
              <Crown className="w-4 h-4 text-foreground" />
            </div>
          )}
        </div>

        <div>
          <h2 className="font-display text-2xl font-bold">{profile.name}</h2>
          <p className="text-muted-foreground text-sm">Evolver since {profile.joinDate.toLocaleDateString()}</p>
        </div>

        <div className="flex justify-center gap-2 flex-wrap">
          <span className={cn("tier-badge bg-gradient-to-r", TIER_COLORS[currentTier])}>{currentTier}</span>
          {skulls > 0 && (
            <span className="tier-badge bg-gradient-to-r from-yellow-500 to-orange-500">💀 x{skulls}</span>
          )}
          {profile.isPremium && isEarlyBird && (
            <span className="tier-badge bg-gradient-to-r from-emerald-500 to-teal-500">🐦 Early Bird</span>
          )}
          {profile.isPremium && (
            <span className="tier-badge bg-gradient-to-r from-yellow-600 to-yellow-500">Premium</span>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border/50">
          <div>
            <p className="text-2xl font-display font-bold text-primary">
              {Math.max(1, Math.floor((Date.now() - profile.joinDate.getTime()) / (1000 * 60 * 60 * 24)))}
            </p>
            <p className="text-xs text-muted-foreground">Days</p>
          </div>
          <div>
            <p className="text-2xl font-display font-bold text-primary">{totalWorkouts}</p>
            <p className="text-xs text-muted-foreground">Workouts</p>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onOpenLeaderboard}
                  className="hover:opacity-70 transition-opacity flex flex-col items-center"
                >
                  <p className="text-2xl font-display font-bold text-primary">{userRank ? `#${userRank}` : "—"}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    Rank <Info className="w-3 h-3" />
                  </p>
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[200px] text-center">
                <p className="text-xs">Rank is based on total workouts completed. Complete more workouts to climb!</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </section>

      {/* Friends Section - Under Rank */}
      {profile.id && (
        <FriendsSection
          userId={profile.id}
          currentUserStats={{
            totalWorkouts,
            daysActive: profile.daysActive,
            badges: userBadgeCount,
          }}
        />
      )}

      {/* Premium Upsell */}
      {!profile.isPremium && (
        <section className="relative overflow-hidden rounded-2xl p-6 evolve-gradient">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer" />
          <div className="relative z-10 space-y-3">
            <div className="flex items-center gap-2">
              <Flame className="w-6 h-6" />
              <h3 className="font-display font-bold text-lg">Unlock Full Power</h3>
            </div>
            <p className="text-sm opacity-90">Your evolution is 100% custom — we're in this together.</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs line-through opacity-50">$19.99/mo</p>
                <span className="text-2xl font-display font-bold">$11.99</span>
                <span className="text-sm opacity-75">/mo forever</span>
              </div>
              <Button
                onClick={onUpgrade}
                className="bg-foreground text-background hover:bg-foreground/90 font-display font-bold"
              >
                Upgrade Now
              </Button>
            </div>
            <p className="text-xs opacity-75 text-center">
              🔥 First 500 get lifetime 40% off — less than a coffee/week!
            </p>
          </div>
        </section>
      )}

      {/* Settings Menu */}
      <section className="glass-card rounded-2xl overflow-hidden">
        <MenuItem
          icon={Users}
          label="Refer a Friend"
          onClick={onOpenReferral}
          badge={referralCount > 0 ? referralCount : undefined}
        />
        <MenuItem
          icon={CreditCard}
          label="Subscription"
          value={
            profile.isPremium
              ? `Premium${subscriptionPlan ? ` • ${subscriptionPlan === "yearly" ? "Yearly" : "Monthly"}` : ""}`
              : "Free"
          }
          subtitle={
            profile.isPremium && subscriptionEnd
              ? `Renews ${format(new Date(subscriptionEnd), "MMM d, yyyy")}`
              : undefined
          }
          onClick={profile.isPremium ? onManageSubscription : onUpgrade}
        />
        <MenuItem icon={Settings} label="Settings" onClick={onOpenSettings} id="settings-button" />
        <MenuItem icon={Lock} label="Change Password" onClick={() => setShowPasswordModal(true)} />
        <MenuItem icon={PlayCircle} label="Replay App Tour" onClick={onReplayTour} />
        <MenuItem icon={FileText} label="Privacy Policy" href="https://evolveaicoach.app/privacy" />
        <MenuItem
          icon={FileText}
          label="Terms & Conditions"
          href="https://app.termly.io/policy-viewer/policy.html?policyUUID=e282e9eb-88ec-44c3-887d-3103b398a5b3"
        />
      </section>

      {/* Feedback Section */}
      <section className="glass-card rounded-2xl p-5 space-y-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-primary" />
          <h3 className="font-display font-bold">Send Feedback</h3>
        </div>
        <p className="text-sm text-muted-foreground">Help us make Evolve better. We read every message.</p>

        {/* Feedback Type */}
        <div className="flex gap-2">
          {(["general", "feature", "bug"] as const).map((type) => (
            <button
              key={type}
              onClick={() => setFeedbackType(type)}
              className={`px-3 py-1.5 text-xs rounded-full border transition-all capitalize ${
                feedbackType === type
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border/50 hover:border-primary/50"
              }`}
            >
              {type === "bug" ? "🐛 Bug" : type === "feature" ? "💡 Feature" : "💬 General"}
            </button>
          ))}
        </div>

        {/* Feedback Text */}
        <Textarea
          placeholder={
            feedbackType === "bug"
              ? "Describe the bug you encountered..."
              : feedbackType === "feature"
                ? "What feature would make Evolve better for you?"
                : "Share your thoughts with us..."
          }
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          className="min-h-[100px] resize-none"
          maxLength={1000}
        />
        <div className="flex justify-between items-center">
          <span className="text-xs text-muted-foreground">{feedback.length}/1000</span>
          <Button onClick={handleSendFeedback} disabled={sendingFeedback || !feedback.trim()} size="sm">
            {sendingFeedback ? (
              "Sending..."
            ) : (
              <>
                <Send className="w-4 h-4 mr-1" />
                Send
              </>
            )}
          </Button>
        </div>
      </section>

      {/* Logout */}
      <section className="glass-card rounded-2xl overflow-hidden">
        <MenuItem icon={LogOut} label="Log Out" onClick={onLogout} danger />
      </section>

      {/* Danger Zone - Delete Account */}
      <DeleteAccountSection userId={profile.id} onLogout={onLogout} />

      {/* Debug panel moved to Admin page - see PlatformDebugPanel */}

      <p className="text-center text-xs text-muted-foreground">Evolve AI Coach v1.0 • Made for Evolvers</p>

      {/* Image Crop Modal */}
      {imageToCrop && (
        <ImageCropModal
          isOpen={showCropModal}
          onClose={() => {
            setShowCropModal(false);
            if (imageToCrop) {
              URL.revokeObjectURL(imageToCrop);
              setImageToCrop(null);
            }
          }}
          imageSrc={imageToCrop}
          onCropComplete={handleCroppedImage}
        />
      )}

      {/* Change Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="relative">
              <Input
                type={showNewPassword ? "text" : "password"}
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="relative">
              <Input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">Password must be at least 6 characters</p>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPasswordModal(false);
                  setNewPassword("");
                  setConfirmNewPassword("");
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button onClick={handleChangePassword} disabled={passwordLoading} className="flex-1">
                {passwordLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Purchase Debug Panel Component
function PurchaseDebugPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const {
    isPremium,
    isInitialized,
    loading,
    lastError,
    paymentProvider,
    isIOS,
    isDespia,
    checkSubscription,
    restorePurchases,
  } = usePlatformPurchase();
  const [checking, setChecking] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const handleCheck = async () => {
    setChecking(true);
    try {
      await checkSubscription();
      toast.success("Subscription check complete - see console for details");
    } catch (e) {
      toast.error("Check failed");
    } finally {
      setChecking(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      await restorePurchases();
      toast.success("Restore complete");
    } catch (e) {
      toast.error("Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button className="w-full flex items-center justify-between px-4 py-3 glass-card rounded-xl text-xs text-muted-foreground hover:text-foreground transition-colors">
          <div className="flex items-center gap-2">
            <Bug className="w-4 h-4" />
            <span>Purchase Debug</span>
          </div>
          <ChevronDown className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 p-4 glass-card rounded-xl space-y-3 text-xs">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-secondary/30 rounded-lg p-2">
              <p className="text-muted-foreground">Premium</p>
              <p className={cn("font-mono font-bold", isPremium ? "text-green-500" : "text-red-500")}>
                {isPremium ? "TRUE" : "FALSE"}
              </p>
            </div>
            <div className="bg-secondary/30 rounded-lg p-2">
              <p className="text-muted-foreground">Provider</p>
              <p className="font-mono font-bold text-primary">{paymentProvider}</p>
            </div>
            <div className="bg-secondary/30 rounded-lg p-2">
              <p className="text-muted-foreground">Initialized</p>
              <p className="font-mono">{isInitialized ? "Yes" : "No"}</p>
            </div>
            <div className="bg-secondary/30 rounded-lg p-2">
              <p className="text-muted-foreground">Platform</p>
              <p className="font-mono">{isDespia ? "Despia" : isIOS ? "iOS Web" : "Web"}</p>
            </div>
          </div>

          {lastError && (
            <div className="bg-destructive/20 rounded-lg p-2 text-destructive">
              <p className="font-medium">Last Error:</p>
              <p className="font-mono text-[10px] break-all">{lastError}</p>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs"
              onClick={handleCheck}
              disabled={checking || loading}
            >
              {checking ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              Check Sub
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs"
              onClick={handleRestore}
              disabled={restoring || loading}
            >
              {restoring ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
              Restore
            </Button>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

interface MenuItemProps {
  icon: React.ElementType;
  label: string;
  value?: string;
  subtitle?: string;
  danger?: boolean;
  onClick?: () => void;
  badge?: number;
  id?: string;
  href?: string;
}

function MenuItem({ icon: Icon, label, value, subtitle, danger, onClick, badge, id, href }: MenuItemProps) {
  const content = (
    <>
      <div className="flex items-center gap-3">
        <Icon className={cn("w-5 h-5", danger ? "text-destructive" : "text-muted-foreground")} />
        <span className={cn("font-medium", danger && "text-destructive")}>{label}</span>
        {badge !== undefined && (
          <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-xs bg-primary/20 text-primary">
            {badge}
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-2 text-right">
        <div className="flex flex-col items-end">
          {value && <span className="text-sm text-muted-foreground">{value}</span>}
          {subtitle && <span className="text-xs text-muted-foreground/70">{subtitle}</span>}
        </div>
        {href ? (
          <ExternalLink className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        )}
      </div>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "w-full flex items-center justify-between px-4 py-4 border-b border-border/30 last:border-0 transition-colors",
          "hover:bg-secondary/50",
        )}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      id={id}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.();
      }}
      className={cn(
        "w-full flex items-center justify-between px-4 py-4 border-b border-border/30 last:border-0 transition-colors",
        danger ? "hover:bg-destructive/10" : "hover:bg-secondary/50",
      )}
    >
      {content}
    </button>
  );
}

// Delete Account Section Component
function DeleteAccountSection({ userId, onLogout }: { userId?: string; onLogout: () => void }) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleDeleteAccount = async () => {
    if (!userId) return;

    setIsDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account");

      if (error) {
        console.error("[Delete Account] Edge function error:", error);
        toast.error("Failed to delete account. Please try again.");
        return;
      }

      if (!data?.success) {
        console.error("[Delete Account] Deletion failed:", data?.error);
        toast.error(data?.error || "Failed to delete account. Please try again.");
        return;
      }

      localStorage.clear();
      sessionStorage.clear();

      await supabase.auth.signOut({ scope: "local" });

      toast.success("Account deleted successfully", {
        description: "All your data has been permanently removed.",
      });

      setIsOpen(false);
      onLogout();

      window.location.href = "/auth";
    } catch (error) {
      console.error("[Delete Account] Unexpected error:", error);
      toast.error("Failed to delete account. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <section className="glass-card rounded-2xl p-5 space-y-3 border border-destructive/20">
      <div className="flex items-center gap-2">
        <Shield className="w-5 h-5 text-destructive" />
        <h3 className="font-display font-bold text-destructive">Danger Zone</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        Permanently delete your account and all associated data
      </p>
      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogTrigger asChild>
          <Button variant="destructive" size="sm" className="w-full" disabled={isDeleting}>
            {isDeleting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            {isDeleting ? "Deleting Account..." : "Delete Account"}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="max-w-md">
          {/* Close button */}
          <button
            onClick={() => setIsOpen(false)}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
          
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Delete Your Account?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3" asChild>
              <div>
                <p>
                  This action <strong>cannot be undone</strong>. This will permanently delete your
                  account and remove all your data including:
                </p>
                <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
                  <li>Profile information</li>
                  <li>Workout sessions & exercise data</li>
                  <li>Nutrition logs & meal history</li>
                  <li>Weight history & progress tracking</li>
                  <li>Chat history & conversations</li>
                  <li>Badges & achievements</li>
                  <li>Friend connections</li>
                  <li>Personal records (PRs)</li>
                </ul>
                <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3 mt-3">
                  <p className="text-xs text-muted-foreground">
                    <strong className="text-destructive">Important:</strong> Deleting your account
                    does not cancel any active Apple subscriptions. Please manage your subscription
                    in <strong>Settings → Apple ID → Subscriptions</strong> on your device.
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete My Account"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
